import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "utils/api";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Dropdown from "components/CustomDropdown/Dropdown";

function AddVideo() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    user: "",
    videoType: "",
    description: "",
    videoSourceType: "",
    videoLink: "",
  });
  const [videoFile, setVideoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear video file and link when source type changes
    if (name === "videoSourceType") {
      setVideoFile(null);
      setFormData((prev) => ({
        ...prev,
        videoLink: "",
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ["video/mp4", "video/mpeg", "video/quicktime", "video/x-msvideo"];
      if (!validTypes.includes(file.type)) {
        toast.error("Please upload a valid video file (MP4, MPEG, MOV, AVI)");
        return;
      }
      // Validate file size (max 100MB)
      if (file.size > 100 * 1024 * 1024) {
        toast.error("Video file size should not exceed 100MB");
        return;
      }
      setVideoFile(file);
    }
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload a valid banner image");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Banner image size should not exceed 5MB");
        return;
      }
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.title.trim()) {
      toast.error("Please enter video title");
      return;
    }
    if (!formData.user) {
      toast.error("Please select user");
      return;
    }
    if (!bannerFile) {
      toast.error("Please upload banner image");
      return;
    }
    if (!formData.videoType) {
      toast.error("Please select video type");
      return;
    }
    if (!formData.videoSourceType) {
      toast.error("Please select video source type");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Please enter description");
      return;
    }

    // Validate based on source type
    if (formData.videoSourceType === "NORMAL") {
      if (!videoFile) {
        toast.error("Please upload a video file");
        return;
      }
    } else if (formData.videoSourceType === "YOUTUBE") {
      if (!formData.videoLink.trim()) {
        toast.error("Please enter YouTube video link");
        return;
      }
    }

    try {
      setLoading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("user", formData.user);
      formDataToSend.append("banner", bannerFile);
      formDataToSend.append("videoType", formData.videoType);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("videoSourceType", formData.videoSourceType);

      if (formData.videoSourceType === "NORMAL" && videoFile) {
        formDataToSend.append("video", videoFile);
      } else if (formData.videoSourceType === "YOUTUBE") {
        formDataToSend.append("videoLink", formData.videoLink);
      }

      const response = await api.post("/videos", formDataToSend, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success) {
        toast.success(response.data.message || "Video added successfully");
        navigate("/allvideos");
      } else {
        toast.error(response.data.message || "Failed to add video");
      }
    } catch (error) {
      console.error("Error adding video:", error);
      toast.error(error.response?.data?.message || "Failed to add video");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <ArgonBox p={3}>
                <ArgonTypography variant="h5" fontWeight="medium" mb={3}>
                  Add New Video
                </ArgonTypography>

                <ArgonBox component="form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* Title */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Title *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        type="text"
                        placeholder="Enter video title"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* User */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          User *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.user}
                        options={[
                          { label: "Select user", value: "" },
                          { label: "Parent", value: "Parent" },
                          { label: "Tutor", value: "Tutor" },
                        ]}
                        onChange={(e) => setFormData({ ...formData, user: e.target.value })}
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Banner */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Banner Image *
                        </ArgonTypography>
                        <ArgonTypography display="block" variant="caption" color="text">
                          Recommended size: 1280 x 720 px (16:9), max 5MB.
                        </ArgonTypography>
                      </ArgonBox>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerChange}
                        style={{
                          width: "100%",
                          padding: "10px",
                          border: "1px solid #d2d6da",
                          borderRadius: "8px",
                          fontSize: "14px",
                        }}
                      />
                      {bannerPreview && (
                        <ArgonBox mt={2}>
                          <img
                            src={bannerPreview}
                            alt="Banner preview"
                            style={{
                              width: "180px",
                              height: "100px",
                              objectFit: "cover",
                              borderRadius: "8px",
                              border: "1px solid #d2d6da",
                            }}
                          />
                        </ArgonBox>
                      )}
                    </Grid>

                    {/* Video Type */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Type *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.videoType}
                        options={[
                          { label: "Select video type", value: "" },
                          { label: "Educational", value: "EDUCATIONAL" },
                          { label: "Tutorial", value: "TUTORIAL" },
                          { label: "Practical", value: "PRACTICAL" },
                        ]}
                        onChange={(e) => setFormData({ ...formData, videoType: e.target.value })}
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Video Source Type */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Video Source Type *
                        </ArgonTypography>
                      </ArgonBox>
                      <Dropdown
                        value={formData.videoSourceType}
                        options={[
                          { label: "Select video source type", value: "" },
                          { label: "Upload Video (Normal)", value: "NORMAL" },
                          { label: "YouTube Link", value: "YOUTUBE" },
                        ]}
                        onChange={(e) =>
                          setFormData({ ...formData, videoSourceType: e.target.value })
                        }
                        width={800}
                        size="small"
                      />
                    </Grid>

                    {/* Conditional: Video Upload or YouTube Link */}
                    {formData.videoSourceType === "NORMAL" && (
                      <Grid item xs={12}>
                        <ArgonBox mb={1}>
                          <ArgonTypography variant="caption" fontWeight="bold" color="text">
                            Upload Video File *
                          </ArgonTypography>
                        </ArgonBox>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={handleFileChange}
                          style={{
                            width: "100%",
                            padding: "10px",
                            border: "1px solid #d2d6da",
                            borderRadius: "8px",
                            fontSize: "14px",
                          }}
                        />
                        {videoFile && (
                          <ArgonTypography variant="caption" color="success" mt={1}>
                            Selected: {videoFile.name}
                          </ArgonTypography>
                        )}
                      </Grid>
                    )}

                    {formData.videoSourceType === "YOUTUBE" && (
                      <Grid item xs={12}>
                        <ArgonBox mb={1}>
                          <ArgonTypography variant="caption" fontWeight="bold" color="text">
                            YouTube Video Link *
                          </ArgonTypography>
                        </ArgonBox>
                        <ArgonInput
                          type="text"
                          placeholder="Enter YouTube video URL"
                          name="videoLink"
                          value={formData.videoLink}
                          onChange={handleChange}
                          fullWidth
                        />
                      </Grid>
                    )}

                    {/* Description */}
                    <Grid item xs={12}>
                      <ArgonBox mb={1}>
                        <ArgonTypography variant="caption" fontWeight="bold" color="text">
                          Description *
                        </ArgonTypography>
                      </ArgonBox>
                      <ArgonInput
                        multiline
                        rows={4}
                        placeholder="Enter video description"
                        name="description"
                        value={formData.description}
                        onChange={handleChange}
                        fullWidth
                      />
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={12}>
                      <ArgonBox display="flex" justifyContent="flex-end" gap={2}>
                        <ArgonButton
                          variant="outlined"
                          color="secondary"
                          onClick={() => navigate("/allvideos")}
                          disabled={loading}
                        >
                          Cancel
                        </ArgonButton>
                        <ArgonButton
                          type="submit"
                          variant="gradient"
                          color="info"
                          disabled={loading}
                        >
                          {loading ? "Adding..." : "Add Video"}
                        </ArgonButton>
                      </ArgonBox>
                    </Grid>
                  </Grid>
                </ArgonBox>
              </ArgonBox>
            </Card>
          </Grid>
        </Grid>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default AddVideo;
